/**
 * Tenant font — mirrors Flutter WebFontConstants.getFontFamily() (web_font_constants.dart).
 *
 * Only themeSettings.primaryFontFamilyName is used (Flutter ignores primaryFontFamilyUrl):
 *   - missing or 'Trebuchet MS' → bundled Trebuchet MS (the default)
 *   - 'Source Sans Pro'         → bundled Source Sans Pro
 *   - anything else             → loaded from Google Fonts (GoogleFonts.getFont)
 *
 * When Google Fonts has no such family, Flutter catches the error and keeps the font it
 * already had, so a failed load falls back to the last font that resolved.
 * The bundled fonts are declared with @font-face in global.css (pubspec.yaml fonts:).
 */

const TREBUCHET_MS = 'Trebuchet MS';
const BUNDLED_FONTS = [TREBUCHET_MS, 'Source Sans Pro'];

const GOOGLE_FONTS_CSS_URL = 'https://fonts.googleapis.com/css2';
/** Every weight the UI uses; Google Fonts skips the ones a family lacks. */
const GOOGLE_FONT_VARIANTS = 'ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,400';

/** WebFontConstants.fontFamily — the last font that resolved. */
let fontFamily = TREBUCHET_MS;
/** Most recently requested family, so a slow Google Fonts response can't override a newer theme. */
let requestedFamily = TREBUCHET_MS;

const googleFontLoads = new Map<string, Promise<boolean>>();

function setFontStack(family: string): void {
  const quoted = `'${family.replace(/'/g, '')}'`;
  const stack = family === TREBUCHET_MS ? `${quoted}, sans-serif` : `${quoted}, '${TREBUCHET_MS}', sans-serif`;
  document.documentElement.style.setProperty('--font-family', stack);
}

/** GoogleFonts.getFont(name) — resolves false when Google Fonts has no such family. */
function loadGoogleFont(family: string): Promise<boolean> {
  let result = googleFontLoads.get(family);
  if (!result) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `${GOOGLE_FONTS_CSS_URL}?family=${encodeURIComponent(family)}:${GOOGLE_FONT_VARIANTS}&display=swap`;
    result = new Promise<boolean>((resolve) => {
      link.onload = () => resolve(true);
      // Google Fonts answers an unknown family with HTTP 400
      link.onerror = () => resolve(false);
    });
    document.head.appendChild(link);
    googleFontLoads.set(family, result);
  }
  return result;
}

/** Sets --font-family from the tenant theme (runs for the cached and the fetched theme). */
export async function applyTenantFont(fontFamilyName: string | undefined): Promise<void> {
  const family = fontFamilyName || TREBUCHET_MS;
  requestedFamily = family;
  setFontStack(family);
  if (BUNDLED_FONTS.includes(family)) {
    fontFamily = family;
    return;
  }

  const loaded = await loadGoogleFont(family);
  if (requestedFamily !== family) return;
  if (loaded) {
    fontFamily = family;
  } else {
    console.warn(`[tenantFont] Font not found: ${family}`);
    setFontStack(fontFamily);
  }
}

/**
 * Font for header/footer text styled with headerFooterConfig `config.fontFamily`.
 * Flutter passes that name to TextStyle as-is without loading it, so it only shows when
 * the font is available; otherwise the text stays in the tenant font. Unset → inherit.
 */
export function configFontStack(family: string | undefined): string | undefined {
  const name = family?.trim();
  if (!name) return undefined;
  const head = name.includes(',') ? name : `'${name.replace(/'/g, '')}'`;
  return `${head}, var(--font-family)`;
}

/**
 * Markup that gives a separate HTML document (the privacy notice's iframe) the app font: the
 * tenant font's stylesheet (Google Fonts) or the bundled @font-face rules, then the current
 * --font-family on every element, ahead of the document's own font-family declarations (tenant
 * notices hard-code e.g. "Times New Roman"). Flutter's HtmlWidget draws the notice in the app font.
 */
export function appFontEmbedHtml(): string {
  const stack =
    getComputedStyle(document.documentElement).getPropertyValue('--font-family').trim() ||
    `'${TREBUCHET_MS}', sans-serif`;

  const links = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>(`link[rel="stylesheet"][href^="${GOOGLE_FONTS_CSS_URL}"]`),
  ).map((link) => `<link rel="stylesheet" href="${link.href.replace(/"/g, '&quot;')}">`);

  // The bundled fonts' @font-face rules (global.css); their /assets URLs resolve against this page
  const faces: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // cross-origin sheet (e.g. Google Fonts) — linked above instead
    }
    for (const rule of Array.from(rules)) {
      if (rule instanceof CSSFontFaceRule) faces.push(rule.cssText);
    }
  }

  return `${links.join('')}<style>${faces.join('\n')}\nhtml, body, body * { font-family: ${stack} !important; }</style>`;
}
